import { Platform } from "@-/com.npmjs.react-native";
import { getGraphicsTier, type GraphicsTier } from "@-/dev.improve.haniverse.lib.graphics-tier";
import {
  welcomeGalaxyMixColor,
  welcomeGalaxyPalette,
  type WelcomeGalaxyAppearance,
  type WelcomeGalaxyChannels,
} from "#welcome-galaxy-palette";
import { WELCOME_GALAXY_TONE_MARKS } from "#welcome-galaxy-tone-marks";
import {
  computeWelcomeGalaxyCamera,
  flightSmootherstep,
  focusSmootherstep,
  smootherstep,
  welcomeGalaxyEntry,
  welcomeGalaxyPlacement,
  welcomeGalaxySpin,
  welcomeGalaxyZoom,
  WELCOME_GALAXY_ENTRY_ZOOM,
  WELCOME_GALAXY_FLIGHT_START_DISTANCE,
  WELCOME_GALAXY_SETTLE_T,
  type WelcomeGalaxyEntryState,
  type WelcomeGalaxyViewport,
} from "#welcome-galaxy-timeline";

/**
 * The welcome galaxy's particles: characters and stars on a two-armed spiral disk, a screen-fixed
 * field of background stars (or, on the light galaxy, tone marks) that streak inwards during the
 * flight and remain faint behind fading outward rays at rest, and a stream of characters along the
 * camera's flight path that rush past it as it drifts backwards out of the galaxy.
 *
 * Everything about a particle that never changes (its place on the disk, its sprite's scale and
 * tilt, its colour, its twinkle's phase) is worked out once, into struct-of-arrays typed arrays.
 * The UI-thread worklet that positions them each frame (`writeWelcomeGalaxySprites`) is then a
 * tight loop of multiply-adds, with no per-particle trigonometry, writing straight into the typed
 * buffers the `Atlas` copies in one go. The layout is disk glyphs, then disk stars, then field
 * stars, then stream glyphs. Every frame is a pure function of `t`, which is what makes the
 * timeline seekable and testable.
 */

export type WelcomeGalaxyParticleBudget = {
  fieldStars: number;
  glyphs: number;
  stars: number;
  streamGlyphs: number;
};

/**
 * Tuned so the glyphs stay legible rather than piling up now the whole disk fits on screen; the
 * per-frame cost is a few multiply-adds per particle. A phone of the `low` graphics tier
 * (`lib/graphics-tier`) draws about half as many, every one of them blended over the sky.
 */
export const WELCOME_GALAXY_PARTICLE_BUDGETS = {
  android: { fieldStars: 120, glyphs: 330, stars: 1100, streamGlyphs: 130 },
  ios: { fieldStars: 140, glyphs: 400, stars: 1400, streamGlyphs: 160 },
  low: { fieldStars: 60, glyphs: 220, stars: 600, streamGlyphs: 80 },
  web: { fieldStars: 100, glyphs: 260, stars: 800, streamGlyphs: 100 },
} as const satisfies Record<string, WelcomeGalaxyParticleBudget>;

export function welcomeGalaxyParticleBudget(
  tier: GraphicsTier = getGraphicsTier(),
): WelcomeGalaxyParticleBudget {
  if (tier === "low") {
    return WELCOME_GALAXY_PARTICLE_BUDGETS.low;
  }
  return Platform.OS === "ios" || Platform.OS === "android" || Platform.OS === "web"
    ? WELCOME_GALAXY_PARTICLE_BUDGETS[Platform.OS]
    : WELCOME_GALAXY_PARTICLE_BUDGETS.web;
}

/**
 * The magnifications over which a particle the camera flies past fades out, gone by the second
 * (see `WelcomeGalaxyParticles.nearFadeStart`). A `low` tier phone lets them go at half the size:
 * the few largest characters near the camera are most of what the flight fills the screen with.
 */
export function welcomeGalaxyNearFade(
  tier: GraphicsTier = getGraphicsTier(),
): readonly [start: number, end: number] {
  return tier === "low" ? LOW_TIER_NEAR_FADE : [NEAR_FADE_START, NEAR_FADE_END];
}

export type WelcomeGalaxyAtlasMetrics = {
  /** Side of every square atlas cell (px). */
  cellSize: number;
  /** Height of the glyph drawn inside a glyph cell (px); the rest is glow padding. */
  glyphSize: number;
  /** Diameter of the star dot's bright part inside the star cell (px). */
  starSize: number;
};

export type WelcomeGalaxyParticles = {
  /**
   * The particle's brightness as an alpha (0–255). Stars twinkle around it as
   * `0.75 alpha + sin(ωt) twinkleSin + cos(ωt) twinkleCos`, which is
   * `alpha (0.75 + 0.25 sin(ωt + phase))` without any per-particle trigonometry.
   */
  alpha: Float32Array;
  /**
   * The offset from the sprite's RSXform translation to its cell's centre, at the settled view's
   * scale: half the cell, scaled and turned like the sprite.
   */
  anchorX: Float32Array;
  anchorY: Float32Array;
  count: number;
  /** A disk particle's height above the disk plane (disk radii). */
  diskHeight: Float32Array;
  /** A disk particle's position in the disk plane, before the spin (disk radii). */
  diskX: Float32Array;
  diskY: Float32Array;
  fieldStarCount: number;
  glyphCount: number;
  /**
   * Magnification at which a particle the camera flies past starts to fade, too close (and too big)
   * to draw, and at which it is gone (`welcomeGalaxyNearFade`).
   */
  nearFadeEnd: number;
  nearFadeStart: number;
  /**
   * A field star's fixed screen position in [-1, 1]², or a stream glyph's sideways offset from the
   * flight path (disk radii, in screen axes).
   */
  offsetX: Float32Array;
  offsetY: Float32Array;
  /** Base colour, 0xRRGGBB: a star's light, or on the light galaxy its ink or pigment. */
  rgb: Uint32Array;
  /** Atlas sprite index: a glyph cell, or `starSpriteIndex`. */
  sprite: Uint16Array;
  starCount: number;
  /** A stream glyph's distance from the core along the flight path (disk radii). */
  streamDepth: Float32Array;
  /** The palette's `streamFarFade`: stream glyphs are gone at the first magnification, whole by the second. */
  streamFarFadeEnd: number;
  streamFarFadeStart: number;
  streamGlyphCount: number;
  twinkleCos: Float32Array;
  twinkleSin: Float32Array;
  /**
   * The sprite's RSXform rotation-and-scale at the settled view's scale: its atlas cell scaled to
   * the particle's on-screen size (glyph height, or star diameter, in dp) and turned by its small
   * resting tilt (glyphs only).
   */
  xformCos: Float32Array;
  xformSin: Float32Array;
};

export type CreateWelcomeGalaxyParticlesOptions = WelcomeGalaxyParticleBudget & {
  /**
   * Which galaxy the colours are for: light on space, or ink and pigment on paper (see
   * `welcome-galaxy-palette.ts`). Either way the particles sit in exactly the same places.
   */
  appearance: WelcomeGalaxyAppearance;
  /** How many glyph cells the atlas has; glyph sprites index into `0..glyphSpriteCount`. */
  glyphSpriteCount: number;
  metrics: WelcomeGalaxyAtlasMetrics;
  /** `welcomeGalaxyNearFade`; the standard one when left out. */
  nearFade?: readonly [start: number, end: number];
  seed: number;
  starSpriteIndex: number;
  /** The first of the four tone marks' cells (ˉ ˊ ˇ ˋ), for a palette that draws them. */
  toneMarkSpriteIndex: number;
};

/** Log-spiral arm pitch angle; smaller winds the arms tighter. */
const ARM_PITCH = (15 * Math.PI) / 180;
const ARM_COUNT = 2;
const ARM_INNER_RADIUS = 0.05;
/** Exponential disk scale length (in disk radii). */
const DISK_SCALE_LENGTH = 0.3;
const ARM_FRACTION = 0.8;
const BULGE_STAR_FRACTION = 0.15;
/** Stream glyphs sit between just behind the starting camera and this far out (disk radii). */
const STREAM_MAX_DEPTH = 2.2;
/** Sideways offsets of stream glyphs from the flight path (disk radii). */
const STREAM_MIN_OFFSET = 0.004;
const STREAM_MAX_OFFSET = 0.035;
/**
 * Sprites grow with magnification raised to this power rather than linearly, so characters
 * brushing past the camera loom large without dissolving into blurry, upscaled blobs.
 */
const SIZE_MAGNIFICATION_EXPONENT = 0.55;
/** Magnification at which a particle is too close (and too big) to draw. */
const NEAR_FADE_START = 28;
const NEAR_FADE_END = 55;
const LOW_TIER_NEAR_FADE = [14, 28] as const;
/** A projection denominator below this is at (or behind) the camera. */
const MIN_DENOMINATOR = 0.02;
/**
 * How hard the background stars are flung outwards per e-fold of zoom: they start scattered well
 * past the screen edges and rush in towards the core as the camera blasts backwards.
 */
const FIELD_WARP = 0.25;
const TAU = 2 * Math.PI;
/** How far (rad, one standard deviation) a background tone mark is tilted. */
const TONE_MARK_TILT = 0.22;
/** The twinkle's angular frequency, ω (rad/s). */
const TWINKLE_RATE = 0.3 * TAU;

/**
 * How far the field stars are pushed out from the vanishing point at `t` (1 once settled). The
 * canvas also reads it a moment earlier to draw each star's warp streak. The zoom back in on
 * entry pushes them out past 1 again, so they streak outwards.
 */
export function welcomeGalaxyFieldParallax(
  t: number,
  entryTime = 0,
  entryZoom = WELCOME_GALAXY_ENTRY_ZOOM,
): number {
  "worklet";
  return 1 + FIELD_WARP * Math.log(welcomeGalaxyZoom(t, entryTime, entryZoom));
}

/** How many disk characters trail a spin streak on entry, and how many chords each arc has. */
export const WELCOME_GALAXY_SPIN_STREAK_COUNT = 320;
export const WELCOME_GALAXY_SPIN_STREAK_SEGMENTS = 6;
/** How far back (s) each spin streak reaches along its character's arc. */
const SPIN_STREAK_LAG = 0.1;

/**
 * Centre line of spiral arm `arm` at `radius`, in the disk frame before the spin. The arms trail:
 * the spin increases the angle, so the angle falls with radius.
 */
export function welcomeGalaxyArmAngle(arm: number, radius: number): number {
  return (
    (arm * 2 * Math.PI) / ARM_COUNT -
    Math.log(Math.max(radius, ARM_INNER_RADIUS) / ARM_INNER_RADIUS) / Math.tan(ARM_PITCH)
  );
}

export const WELCOME_GALAXY_ARM_COUNT = ARM_COUNT;

/** mulberry32: a tiny seeded PRNG, so the galaxy looks the same on every launch. */
export function createWelcomeGalaxyRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d_2b_79_f5) >>> 0;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4_294_967_296;
  };
}

export function createWelcomeGalaxyGaussian(random: () => number): () => number {
  return () => Math.sqrt(-2 * Math.log(1 - random())) * Math.cos(2 * Math.PI * random());
}

/**
 * A 0–1 colour channel as a byte, exactly as Skia converts a float colour: through float32, then
 * truncated.
 */
export function welcomeGalaxyColorByte(channel: number): number {
  return Math.trunc(Math.fround(Math.fround(channel) * 255));
}

export function createWelcomeGalaxyParticles({
  appearance,
  fieldStars,
  glyphs,
  glyphSpriteCount,
  metrics,
  nearFade = [NEAR_FADE_START, NEAR_FADE_END],
  seed,
  stars,
  starSpriteIndex,
  streamGlyphs,
  toneMarkSpriteIndex,
}: CreateWelcomeGalaxyParticlesOptions): WelcomeGalaxyParticles {
  const random = createWelcomeGalaxyRandom(seed);
  const gaussian = createWelcomeGalaxyGaussian(random);
  const count = glyphs + stars + fieldStars + streamGlyphs;
  const palette = welcomeGalaxyPalette(appearance);
  const floats = () => new Float32Array(count);
  const particles: WelcomeGalaxyParticles = {
    alpha: floats(),
    anchorX: floats(),
    anchorY: floats(),
    count,
    diskHeight: floats(),
    diskX: floats(),
    diskY: floats(),
    fieldStarCount: fieldStars,
    glyphCount: glyphs,
    nearFadeEnd: nearFade[1],
    nearFadeStart: nearFade[0],
    offsetX: floats(),
    offsetY: floats(),
    rgb: new Uint32Array(count),
    sprite: new Uint16Array(count),
    starCount: stars,
    streamDepth: floats(),
    streamFarFadeEnd: palette.streamFarFade[1],
    streamFarFadeStart: palette.streamFarFade[0],
    streamGlyphCount: streamGlyphs,
    twinkleCos: floats(),
    twinkleSin: floats(),
    xformCos: floats(),
    xformSin: floats(),
  };
  const half = metrics.cellSize / 2;
  const { fieldStarsAreToneMarks, particleColors: colors } = palette;

  const setColor = (index: number, [red, green, blue]: WelcomeGalaxyChannels) => {
    particles.rgb[index] =
      (welcomeGalaxyColorByte(red) << 16) |
      (welcomeGalaxyColorByte(green) << 8) |
      welcomeGalaxyColorByte(blue);
  };
  /** The sprite: `size` dp tall (glyphs) or across (stars), turned by `tilt`. */
  const setSprite = (index: number, size: number, spriteSize: number, tilt: number) => {
    const scale = size / spriteSize;
    const scaledCos = scale * Math.cos(tilt);
    const scaledSin = scale * Math.sin(tilt);
    particles.xformCos[index] = scaledCos;
    particles.xformSin[index] = scaledSin;
    particles.anchorX[index] = half * (scaledCos - scaledSin);
    particles.anchorY[index] = half * (scaledSin + scaledCos);
  };
  const setBrightness = (index: number, brightness: number, twinklePhase: number) => {
    const alpha = 255 * brightness;
    particles.alpha[index] = alpha;
    particles.twinkleSin[index] = 0.25 * alpha * Math.cos(TAU * twinklePhase);
    particles.twinkleCos[index] = 0.25 * alpha * Math.sin(TAU * twinklePhase);
  };
  const setDiskPosition = (index: number, radius: number, angle: number, height: number) => {
    particles.diskX[index] = radius * Math.cos(angle);
    particles.diskY[index] = radius * Math.sin(angle);
    particles.diskHeight[index] = height;
  };

  /** A disk character's or star's colour: the core's at the centre, the rim's at r = 1. */
  const diskColor = (radius: number) =>
    welcomeGalaxyMixColor(colors.diskCore, colors.diskRim, smootherstep(0, 1, radius));

  /** Exponential disk radius by inverse CDF, truncated at r = 1. */
  const diskRadius = () =>
    -DISK_SCALE_LENGTH * Math.log(1 - random() * (1 - Math.exp(-1 / DISK_SCALE_LENGTH)));

  /** An angle on (or, for a fifth of particles, anywhere off) an arm at `radius`. */
  const diskAngle = (radius: number, spread: number) => {
    const armAngle = welcomeGalaxyArmAngle(Math.floor(random() * ARM_COUNT), radius);
    return random() < ARM_FRACTION
      ? armAngle + gaussian() * spread * (1 - 0.5 * radius)
      : random() * 2 * Math.PI;
  };

  for (let index = 0; index < glyphs; index += 1) {
    // Glyphs stay out of the very core, which is stars and glow, so they never pile into a blob.
    const radius = 0.14 + 0.86 * diskRadius();
    const angle = diskAngle(radius, 0.28);
    setDiskPosition(index, radius, angle, gaussian() * 0.02);
    const size = 8 + 8 * random() ** 0.7 * (1 - 0.35 * radius);
    // The most common characters cluster towards the core.
    particles.sprite[index] = Math.min(
      glyphSpriteCount - 1,
      Math.floor(glyphSpriteCount * random() ** (1 + 1.2 * (1 - radius))),
    );
    setSprite(index, size, metrics.glyphSize, gaussian() * 0.18);
    const brightness = 0.6 + 0.4 * random();
    setBrightness(index, brightness, random());
    setColor(index, diskColor(radius));
  }

  for (let index = glyphs; index < glyphs + stars; index += 1) {
    const isBulge = random() < BULGE_STAR_FRACTION;
    let radius: number;
    if (isBulge) {
      radius = Math.min(1, 0.12 * Math.sqrt(-2 * Math.log(1 - random())));
      setDiskPosition(index, radius, random() * 2 * Math.PI, gaussian() * 0.08);
    } else {
      radius = diskRadius();
      const angle = diskAngle(radius, 0.32);
      setDiskPosition(index, radius, angle, gaussian() * 0.03);
    }
    setSprite(index, 0.8 + 1.6 * random() ** 2.5, metrics.starSize, 0);
    particles.sprite[index] = starSpriteIndex;
    const brightness = 0.35 + 0.65 * random();
    setBrightness(index, brightness, random());
    setColor(index, diskColor(radius));
  }

  const fieldStart = glyphs + stars;
  for (let index = fieldStart; index < fieldStart + fieldStars; index += 1) {
    particles.offsetX[index] = random() * 2 - 1;
    particles.offsetY[index] = random() * 2 - 1;
    const sizeRandom = random();
    if (fieldStarsAreToneMarks) {
      // Star-sized, in the glyph box like a character, and tilted only so far that each still
      // reads as its tone.
      setSprite(index, 3 + 3 * sizeRandom ** 2, metrics.glyphSize, gaussian() * TONE_MARK_TILT);
      particles.sprite[index] = toneMarkSpriteIndex + (index % WELCOME_GALAXY_TONE_MARKS.length);
    } else {
      setSprite(index, 0.8 + 1.8 * sizeRandom ** 3, metrics.starSize, 0);
      particles.sprite[index] = starSpriteIndex;
    }
    const brightness = 0.3 + 0.7 * random();
    setBrightness(index, brightness, random());
    setColor(index, welcomeGalaxyMixColor(colors.fieldDeep, colors.fieldPale, random()));
  }

  for (let index = fieldStart + fieldStars; index < count; index += 1) {
    // Log-uniform depth, so glyphs pass the (exponentially receding) camera at an even rate.
    particles.streamDepth[index] =
      WELCOME_GALAXY_FLIGHT_START_DISTANCE *
      (STREAM_MAX_DEPTH / WELCOME_GALAXY_FLIGHT_START_DISTANCE) ** random();
    const offset =
      palette.streamSpread *
      STREAM_MIN_OFFSET *
      (STREAM_MAX_OFFSET / STREAM_MIN_OFFSET) ** random();
    const angle = random() * 2 * Math.PI;
    particles.offsetX[index] = offset * Math.cos(angle);
    particles.offsetY[index] = offset * Math.sin(angle);
    const size = 9 + 7 * random();
    particles.sprite[index] = Math.min(
      glyphSpriteCount - 1,
      Math.floor(glyphSpriteCount * random() ** 1.6),
    );
    setSprite(index, size, metrics.glyphSize, gaussian() * 0.25);
    setBrightness(index, 0.55 + 0.45 * random(), 0);
    setColor(index, welcomeGalaxyMixColor(colors.streamPale, colors.streamDeep, random()));
  }

  return particles;
}

/** Everything a frame needs that doesn't vary per particle, computed once per frame. */
export type WelcomeGalaxyFrameState = {
  /** Screen position of the galaxy core (the vanishing point of the flight). */
  centerX: number;
  centerY: number;
  /**
   * A disk particle at `(x, y)` in the disk plane and `h` above it projects to
   * `(xx x + xy y + xh h, yx x + yy y + yh h) · magnification` from the core, where the
   * magnification is `zoom / (1 - (dx x + dy y + dh h))`: the spin, the tilt, the on-screen
   * rotation, the disk's size and the perspective, folded into one matrix per frame.
   */
  dh: number;
  dx: number;
  dy: number;
  /** The entry into the Haniverse, once it has begun (`entryTime` > 0). */
  entry: WelcomeGalaxyEntryState | null;
  /** The disk sprites fade in at the start and stay sharp throughout the entry zoom. */
  fadeIn: number;
  /** The field stars' opacity, which also fades with the entry's sky, so none hang over the app. */
  fieldOpacity: number;
  /** How far the settled field stars are pushed out from the vanishing point (1 once settled). */
  fieldParallax: number;
  idle: number;
  /** The camera has stopped: no perspective left, and unit magnification. */
  isSettled: boolean;
  /**
   * Perspective strength: a point `depth` disk radii towards the camera is magnified by
   * `zoom / (1 - perspective * depth)`.
   */
  perspective: number;
  /** The settled disk radius on screen (dp). */
  radius: number;
  /** Fades the stream out before the settled galaxy, so none linger in front of it. */
  streamOpacity: number;
  /** cos and sin of the twinkle's phase, ωt. */
  twinkleCos: number;
  twinkleSin: number;
  viewport: WelcomeGalaxyViewport;
  xh: number;
  xx: number;
  xy: number;
  yh: number;
  yx: number;
  yy: number;
  /** Magnification of the core's depth relative to the settled view (1 once settled). */
  zoom: number;
};

/** The background stars fade in as the flight gets going, so they're there to streak past. */
export function welcomeGalaxyFieldOpacity(t: number): number {
  "worklet";
  return flightSmootherstep(0.04, 0.4, t);
}

/**
 * `spinOffset` is the user's dragged spin (rad), on top of the timeline's idle spin; `entryTime`
 * is how far into the entry into the Haniverse the camera is (0 until someone signs in), and
 * `entryZoom` how far it zooms back in by the end (see `welcomeGalaxyEntryZoomFor`).
 */
export function computeWelcomeGalaxyFrameState(
  t: number,
  viewport: WelcomeGalaxyViewport,
  spinOffset = 0,
  entryTime = 0,
  entryZoom = WELCOME_GALAXY_ENTRY_ZOOM,
): WelcomeGalaxyFrameState {
  "worklet";
  const camera = computeWelcomeGalaxyCamera(t, viewport, entryTime, entryZoom);
  const entry = entryTime > 0 ? welcomeGalaxyEntry(entryTime) : null;
  // Once the flight is over the camera sits exactly at its final distance, where the view is
  // orthographic; pin that rather than carry the last bit of floating-point noise.
  const isSettled = entry === null && t >= WELCOME_GALAXY_SETTLE_T;
  const perspective = isSettled ? 0 : camera.perspective;
  const radius = camera.scale / camera.zoom;
  // Hold the orientation present at sign-in, including any drag, for the entire zoom.
  const spin = welcomeGalaxySpin(t - entryTime) + spinOffset;
  const cosSpin = Math.cos(spin);
  const sinSpin = Math.sin(spin);
  const { cosInclination, sinInclination } = camera;
  const cosPositionAngle = Math.cos(camera.positionAngle);
  const sinPositionAngle = Math.sin(camera.positionAngle);
  const twinklePhase = TWINKLE_RATE * t;
  return {
    centerX: camera.centerX,
    centerY: camera.centerY,
    // Spin within the disk, tilt about the x axis, then rotate on screen by the position angle.
    dh: perspective * cosInclination,
    dx: perspective * sinInclination * sinSpin,
    dy: perspective * sinInclination * cosSpin,
    entry,
    fadeIn: smootherstep(0, 0.2, t) * (entry === null ? 1 : entry.spriteOpacity),
    fieldOpacity: welcomeGalaxyFieldOpacity(t) * (entry === null ? 1 : entry.skyOpacity),
    fieldParallax: welcomeGalaxyFieldParallax(t, entryTime, entryZoom),
    idle: smootherstep(WELCOME_GALAXY_SETTLE_T - 0.5, WELCOME_GALAXY_SETTLE_T + 0.4, t),
    isSettled,
    perspective,
    radius,
    // Gone before the galaxy settles into focus, so none linger in front of it.
    streamOpacity: 1 - focusSmootherstep(0.7, 0.92, t),
    twinkleCos: Math.cos(twinklePhase),
    twinkleSin: Math.sin(twinklePhase),
    viewport,
    xh: radius * sinPositionAngle * sinInclination,
    xx: radius * (cosPositionAngle * cosSpin - sinPositionAngle * cosInclination * sinSpin),
    xy: -radius * (cosPositionAngle * sinSpin + sinPositionAngle * cosInclination * cosSpin),
    yh: -radius * cosPositionAngle * sinInclination,
    yx: radius * (sinPositionAngle * cosSpin + cosPositionAngle * cosInclination * sinSpin),
    yy: radius * (cosPositionAngle * cosInclination * cosSpin - sinPositionAngle * sinSpin),
    zoom: isSettled ? 1 : camera.zoom,
  };
}

/**
 * Hides sprites `from`–`to`: zero-sized as well as transparent, so the GPU rasterises nothing for
 * them.
 */
export function clearWelcomeGalaxySprites(
  transforms: Float32Array,
  colors: Uint32Array,
  from: number,
  to: number,
): void {
  "worklet";
  if (from < to) {
    transforms.fill(0, 4 * from, 4 * to);
    colors.fill(0, from, to);
  }
}

/**
 * Disk particles `from`–`to` (glyphs, or with `twinkles` stars, which twinkle once the galaxy
 * idles). Each writes its `Atlas` RSXform `[scos, ssin, tx, ty]` into `transforms` (mapping its
 * atlas cell, anchored at the centre, onto the screen) and its 0xAARRGGBB colour into `colors`.
 */
export function writeWelcomeGalaxyDiskSprites(
  state: WelcomeGalaxyFrameState,
  particles: WelcomeGalaxyParticles,
  transforms: Float32Array,
  colors: Uint32Array,
  from: number,
  to: number,
  twinkles: boolean,
): void {
  "worklet";
  if (from >= to) {
    return;
  }
  if (state.fadeIn <= 0) {
    clearWelcomeGalaxySprites(transforms, colors, from, to);
    return;
  }
  const { centerX, centerY, dh, dx, dy, fadeIn, xh, xx, xy, yh, yx, yy, zoom } = state;
  const { alpha, anchorX, anchorY, diskHeight, diskX, diskY, rgb, xformCos, xformSin } = particles;
  const { twinkleCos, twinkleSin } = particles;
  // Stars ease from steady light into a twinkle as the galaxy idles: alpha · lerp(1, twinkle,
  // idle), split into the steady part and the twinkle's sin/cos terms.
  const idle = twinkles ? state.idle : 0;
  const steady = 1 - 0.25 * idle;
  const sinTerm = idle * state.twinkleSin;
  const cosTerm = idle * state.twinkleCos;

  if (state.isSettled) {
    // No perspective: every sprite keeps its settled size, and only moves with the spin.
    for (let index = from; index < to; index += 1) {
      const x = diskX[index];
      const y = diskY[index];
      const h = diskHeight[index];
      const base = 4 * index;
      transforms[base] = xformCos[index];
      transforms[base + 1] = xformSin[index];
      transforms[base + 2] = centerX + xx * x + xy * y + xh * h - anchorX[index];
      transforms[base + 3] = centerY + yx * x + yy * y + yh * h - anchorY[index];
      colors[index] =
        ((steady * alpha[index] + sinTerm * twinkleSin[index] + cosTerm * twinkleCos[index]) <<
          24) |
        rgb[index];
    }
    return;
  }

  // Project through the flying camera; a point whose denominator reaches 0 has not yet passed it
  // (or is right on top of it), and anything about to fill the screen fades out up close.
  const { nearFadeEnd, nearFadeStart } = particles;
  for (let index = from; index < to; index += 1) {
    const x = diskX[index];
    const y = diskY[index];
    const h = diskHeight[index];
    const base = 4 * index;
    const denominator = 1 - (dx * x + dy * y + dh * h);
    const magnification = zoom / denominator;
    if (denominator <= MIN_DENOMINATOR || magnification >= nearFadeEnd) {
      transforms[base] = 0;
      transforms[base + 1] = 0;
      colors[index] = 0;
      continue;
    }
    let near = fadeIn;
    if (magnification > nearFadeStart) {
      const fade = (magnification - nearFadeStart) / (nearFadeEnd - nearFadeStart);
      near *= 1 - fade * fade * fade * (fade * (fade * 6 - 15) + 10);
    }
    const opacity =
      near * (steady * alpha[index] + sinTerm * twinkleSin[index] + cosTerm * twinkleCos[index]);
    if (opacity < 1) {
      transforms[base] = 0;
      transforms[base + 1] = 0;
      colors[index] = 0;
      continue;
    }
    const scale = magnification ** SIZE_MAGNIFICATION_EXPONENT;
    transforms[base] = scale * xformCos[index];
    transforms[base + 1] = scale * xformSin[index];
    transforms[base + 2] =
      centerX + (xx * x + xy * y + xh * h) * magnification - scale * anchorX[index];
    transforms[base + 3] =
      centerY + (yx * x + yy * y + yh * h) * magnification - scale * anchorY[index];
    colors[index] = (opacity << 24) | rgb[index];
  }
}

/**
 * The settled disk's per-sprite constants for one viewport. Once the camera has stopped, a disk
 * sprite sits at a fixed offset (the core, plus its height's projection, which the spin never
 * changes, less its anchor) plus its spun position in the disk plane.
 */
export type WelcomeGalaxySettledDisk = {
  offsetX: Float32Array;
  offsetY: Float32Array;
  viewport: WelcomeGalaxyViewport;
};

export function createWelcomeGalaxySettledDisk(
  state: WelcomeGalaxyFrameState,
  particles: WelcomeGalaxyParticles,
): WelcomeGalaxySettledDisk {
  "worklet";
  const count = particles.glyphCount + particles.starCount;
  const offsetX = new Float32Array(count);
  const offsetY = new Float32Array(count);
  const { centerX, centerY, xh, yh } = state;
  const { anchorX, anchorY, diskHeight } = particles;
  for (let index = 0; index < count; index += 1) {
    offsetX[index] = centerX + xh * diskHeight[index] - anchorX[index];
    offsetY[index] = centerY + yh * diskHeight[index] - anchorY[index];
  }
  return { offsetX, offsetY, viewport: state.viewport };
}

/**
 * The settled galaxy's steady state, every frame it idles: with the camera stopped only the spin
 * moves the disk sprites, and only the stars' twinkle changes their colours. So this rewrites just
 * the disk sprites' positions and the stars' colours; the sprites must already hold a settled
 * frame (`writeWelcomeGalaxyDiskSprites` at a settled `state`).
 */
export function writeWelcomeGalaxySettledDiskSprites(
  state: WelcomeGalaxyFrameState,
  particles: WelcomeGalaxyParticles,
  settled: WelcomeGalaxySettledDisk,
  transforms: Float32Array,
  colors: Uint32Array,
): void {
  "worklet";
  const { xx, xy, yx, yy } = state;
  const { diskX, diskY } = particles;
  const { offsetX, offsetY } = settled;
  const starsStart = particles.glyphCount;
  const diskEnd = starsStart + particles.starCount;
  for (let index = 0; index < diskEnd; index += 1) {
    const x = diskX[index];
    const y = diskY[index];
    const base = 4 * index;
    transforms[base + 2] = offsetX[index] + xx * x + xy * y;
    transforms[base + 3] = offsetY[index] + yx * x + yy * y;
  }
  const { alpha, rgb, twinkleCos, twinkleSin } = particles;
  const steady = 1 - 0.25 * state.idle;
  const sinTerm = state.idle * state.twinkleSin;
  const cosTerm = state.idle * state.twinkleCos;
  for (let index = starsStart; index < diskEnd; index += 1) {
    colors[index] =
      ((steady * alpha[index] + sinTerm * twinkleSin[index] + cosTerm * twinkleCos[index]) << 24) |
      rgb[index];
  }
}

/**
 * Field stars `from`–`to`: pushed out from the vanishing point while the camera is still close,
 * so the starfield visibly converges as the camera draws back. They settle into a subtle twinkle
 * behind the idle shoot-out lines. `writePositions: false` updates only their colours when the sprites
 * already hold this parallax.
 */
export function writeWelcomeGalaxyFieldSprites(
  state: WelcomeGalaxyFrameState,
  particles: WelcomeGalaxyParticles,
  transforms: Float32Array,
  colors: Uint32Array,
  from: number,
  to: number,
  writePositions = true,
): void {
  "worklet";
  if (from >= to) {
    return;
  }
  const opacity = state.fieldOpacity * (1 - 0.88 * state.idle);
  if (opacity <= 0) {
    clearWelcomeGalaxySprites(transforms, colors, from, to);
    return;
  }
  const { alpha, rgb, twinkleCos, twinkleSin } = particles;
  const steady = 0.75 * opacity;
  const sinTerm = opacity * state.twinkleSin;
  const cosTerm = opacity * state.twinkleCos;
  for (let index = from; index < to; index += 1) {
    colors[index] =
      ((steady * alpha[index] + sinTerm * twinkleSin[index] + cosTerm * twinkleCos[index]) << 24) |
      rgb[index];
  }
  if (!writePositions) {
    return;
  }
  const { centerX, centerY, fieldParallax, viewport } = state;
  const { anchorX, anchorY, offsetX, offsetY, xformCos, xformSin } = particles;
  const halfWidth = viewport.width / 2;
  const halfHeight = viewport.height / 2;
  for (let index = from; index < to; index += 1) {
    const base = 4 * index;
    transforms[base] = xformCos[index];
    transforms[base + 1] = xformSin[index];
    transforms[base + 2] =
      centerX + (halfWidth * (1 + offsetX[index]) - centerX) * fieldParallax - anchorX[index];
    transforms[base + 3] =
      centerY + (halfHeight * (1 + offsetY[index]) - centerY) * fieldParallax - anchorY[index];
  }
}

/**
 * Stream glyphs `from`–`to`: strung along the flight path, they loom past the camera and dwindle
 * away towards the vanishing point, fading out again once small.
 */
export function writeWelcomeGalaxyStreamSprites(
  state: WelcomeGalaxyFrameState,
  particles: WelcomeGalaxyParticles,
  transforms: Float32Array,
  colors: Uint32Array,
  from: number,
  to: number,
): void {
  "worklet";
  if (from >= to) {
    return;
  }
  const opacity = state.streamOpacity * state.fadeIn;
  if (opacity <= 0) {
    clearWelcomeGalaxySprites(transforms, colors, from, to);
    return;
  }
  const { centerX, centerY, perspective, radius, zoom } = state;
  const { alpha, anchorX, anchorY, offsetX, offsetY, rgb, streamDepth, xformCos, xformSin } =
    particles;
  // They fade out again once they've dwindled towards the vanishing point.
  const { nearFadeEnd, nearFadeStart, streamFarFadeEnd, streamFarFadeStart } = particles;
  for (let index = from; index < to; index += 1) {
    const base = 4 * index;
    const denominator = 1 - perspective * streamDepth[index];
    const magnification = zoom / denominator;
    if (
      denominator <= MIN_DENOMINATOR ||
      magnification >= nearFadeEnd ||
      magnification <= streamFarFadeStart
    ) {
      transforms[base] = 0;
      transforms[base + 1] = 0;
      colors[index] = 0;
      continue;
    }
    let fade = opacity * alpha[index];
    if (magnification > nearFadeStart) {
      const near = (magnification - nearFadeStart) / (nearFadeEnd - nearFadeStart);
      fade *= 1 - near * near * near * (near * (near * 6 - 15) + 10);
    } else if (magnification < streamFarFadeEnd) {
      const far = (magnification - streamFarFadeStart) / (streamFarFadeEnd - streamFarFadeStart);
      fade *= far * far * far * (far * (far * 6 - 15) + 10);
    }
    if (fade < 1) {
      transforms[base] = 0;
      transforms[base + 1] = 0;
      colors[index] = 0;
      continue;
    }
    const scale = magnification ** SIZE_MAGNIFICATION_EXPONENT;
    const reach = radius * magnification;
    transforms[base] = scale * xformCos[index];
    transforms[base + 1] = scale * xformSin[index];
    transforms[base + 2] = centerX + offsetX[index] * reach - scale * anchorX[index];
    transforms[base + 3] = centerY + offsetY[index] * reach - scale * anchorY[index];
    colors[index] = (fade << 24) | rgb[index];
  }
}

/**
 * Writes the sprites of particles `from`–`to` (all of them by default) for the frame: an `Atlas`
 * RSXform per particle into `transforms`, and a 0xAARRGGBB colour into `colors`.
 */
export function writeWelcomeGalaxySprites(
  state: WelcomeGalaxyFrameState,
  particles: WelcomeGalaxyParticles,
  transforms: Float32Array,
  colors: Uint32Array,
  from = 0,
  to = particles.count,
): void {
  "worklet";
  const starsStart = particles.glyphCount;
  const fieldStart = starsStart + particles.starCount;
  const streamStart = fieldStart + particles.fieldStarCount;
  const range = (start: number, end: number) => [Math.max(from, start), Math.min(to, end)] as const;
  const [glyphsFrom, glyphsTo] = range(0, starsStart);
  const [starsFrom, starsTo] = range(starsStart, fieldStart);
  const [fieldFrom, fieldTo] = range(fieldStart, streamStart);
  const [streamFrom, streamTo] = range(streamStart, particles.count);
  writeWelcomeGalaxyDiskSprites(state, particles, transforms, colors, glyphsFrom, glyphsTo, false);
  writeWelcomeGalaxyDiskSprites(state, particles, transforms, colors, starsFrom, starsTo, true);
  writeWelcomeGalaxyFieldSprites(state, particles, transforms, colors, fieldFrom, fieldTo);
  writeWelcomeGalaxyStreamSprites(state, particles, transforms, colors, streamFrom, streamTo);
}

/**
 * The walls the entry's spin streaks are held within once the disc is squaring off into the
 * card (window coordinates, dp): a streak that would cross one runs along it instead.
 */
export type WelcomeGalaxyStreakWalls = {
  bottom: number;
  left: number;
  right: number;
  top: number;
};

/**
 * The entry's spin streaks: the first `WELCOME_GALAXY_SPIN_STREAK_COUNT` disk characters each
 * trail an arc back along their orbit to where they were `SPIN_STREAK_LAG` ago, which grows with
 * the spin until the whole disk is a blur of rings. Each arc is
 * `WELCOME_GALAXY_SPIN_STREAK_SEGMENTS` chords, the half nearest the character into `near` and
 * the rest into `far` (drawn fainter), as `[headX, headY, tailX, tailY]` line segments. Returns
 * how many floats it wrote to each. The arcs are rotated by recurrence rather than per-particle
 * trigonometry, and projected like the disk sprites (ignoring their slight height). With
 * `walls`, every point is held inside them, so the rings flatten against the card's sides and
 * run along them like a fluid against its container rather than passing through.
 */
export function writeWelcomeGalaxySpinStreaks(
  state: WelcomeGalaxyFrameState,
  particles: WelcomeGalaxyParticles,
  near: Float32Array,
  far: Float32Array,
  walls: WelcomeGalaxyStreakWalls | null = null,
): [nearLength: number, farLength: number] {
  "worklet";
  const { entry } = state;
  if (entry === null || entry.spinStreakOpacity <= 0) {
    return [0, 0];
  }
  const segments = WELCOME_GALAXY_SPIN_STREAK_SEGMENTS;
  const nearSegments = segments / 2;
  const count = Math.min(WELCOME_GALAXY_SPIN_STREAK_COUNT, particles.glyphCount);
  const { centerX, centerY, dx, dy, xx, xy, yx, yy, zoom } = state;
  const { diskX, diskY } = particles;
  const left = walls === null ? -Infinity : walls.left;
  const right = walls === null ? Infinity : walls.right;
  const top = walls === null ? -Infinity : walls.top;
  const bottom = walls === null ? Infinity : walls.bottom;
  // Each chord steps this far back along the orbit; the spin runs the disk's angle forwards.
  const step = (entry.spinSpeed * SPIN_STREAK_LAG) / segments;
  const cosStep = Math.cos(step);
  const sinStep = Math.sin(step);
  let nearLength = 0;
  let farLength = 0;
  for (let index = 0; index < count; index += 1) {
    let x = diskX[index];
    let y = diskY[index];
    let magnification = zoom / (1 - (dx * x + dy * y));
    let headX = Math.min(Math.max(centerX + (xx * x + xy * y) * magnification, left), right);
    let headY = Math.min(Math.max(centerY + (yx * x + yy * y) * magnification, top), bottom);
    for (let segment = 0; segment < segments; segment += 1) {
      const rotatedX = x * cosStep + y * sinStep;
      y = y * cosStep - x * sinStep;
      x = rotatedX;
      magnification = zoom / (1 - (dx * x + dy * y));
      const tailX = Math.min(Math.max(centerX + (xx * x + xy * y) * magnification, left), right);
      const tailY = Math.min(Math.max(centerY + (yx * x + yy * y) * magnification, top), bottom);
      if (segment < nearSegments) {
        near[nearLength] = headX;
        near[nearLength + 1] = headY;
        near[nearLength + 2] = tailX;
        near[nearLength + 3] = tailY;
        nearLength += 4;
      } else {
        far[farLength] = headX;
        far[farLength + 1] = headY;
        far[farLength + 2] = tailX;
        far[farLength + 3] = tailY;
        farLength += 4;
      }
      headX = tailX;
      headY = tailY;
    }
  }
  return [nearLength, farLength];
}

/**
 * Each background star's warp streak, from where it is at parallax `now` back to where it was at
 * `past`, as `[headX, headY, tailX, tailY]` line segments into `points`. Returns how many floats
 * it wrote.
 */
export function writeWelcomeGalaxyWarpStreaks(
  particles: WelcomeGalaxyParticles,
  viewport: WelcomeGalaxyViewport,
  now: number,
  past: number,
  points: Float32Array,
): number {
  "worklet";
  const { centerX, centerY } = welcomeGalaxyPlacement(viewport);
  const { offsetX, offsetY } = particles;
  const fieldStart = particles.glyphCount + particles.starCount;
  const halfWidth = viewport.width / 2;
  const halfHeight = viewport.height / 2;
  let length = 0;
  for (let index = fieldStart; index < fieldStart + particles.fieldStarCount; index += 1) {
    const fromCenterX = halfWidth * (1 + offsetX[index]) - centerX;
    const fromCenterY = halfHeight * (1 + offsetY[index]) - centerY;
    points[length] = centerX + fromCenterX * now;
    points[length + 1] = centerY + fromCenterY * now;
    points[length + 2] = centerX + fromCenterX * past;
    points[length + 3] = centerY + fromCenterY * past;
    length += 4;
  }
  return length;
}

/**
 * Repeating rays shooting from the core past the screen edges, even after the camera settles.
 * Seeded directions, staggered phases and varied travel times keep the flow continuous; each
 * tail clears the screen before its ray respawns. Writes at most 48 line segments.
 */
export function writeWelcomeGalaxyShootOutStreaks(
  particles: WelcomeGalaxyParticles,
  viewport: WelcomeGalaxyViewport,
  t: number,
  points: Float32Array,
): number {
  "worklet";
  const { centerX, centerY } = welcomeGalaxyPlacement(viewport);
  const fieldStart = particles.glyphCount + particles.starCount;
  const stride = Math.max(1, Math.ceil(particles.fieldStarCount / 48));
  let length = 0;
  for (let index = fieldStart; index < fieldStart + particles.fieldStarCount; index += stride) {
    const x = (viewport.width / 2) * (1 + particles.offsetX[index]) - centerX;
    const y = (viewport.height / 2) * (1 + particles.offsetY[index]) - centerY;
    const edge = Math.min(
      x === 0 ? Infinity : (x > 0 ? viewport.width - centerX : centerX) / Math.abs(x),
      y === 0 ? Infinity : (y > 0 ? viewport.height - centerY : centerY) / Math.abs(y),
    );
    if (!Number.isFinite(edge)) {
      continue;
    }
    const ray = (index - fieldStart) / stride;
    const duration = 2.4 + 1.2 * Math.abs(particles.offsetX[index]);
    const phase = (Math.max(0, t) / duration + ray * 0.61803398875) % 1;
    const head = edge * (0.12 + 1.3 * phase ** 1.8);
    const tail = edge * (0.12 + 1.3 * Math.max(0, phase - 0.085) ** 1.8);
    points[length] = centerX + x * head;
    points[length + 1] = centerY + y * head;
    points[length + 2] = centerX + x * tail;
    points[length + 3] = centerY + y * tail;
    length += 4;
  }
  return length;
}
